import { token } from '@/infrastructure/container';
import { type Voice } from '@/domain/book';

export interface SpeakOptions {
  rate: number;
  voiceId?: string;
}

export interface SpeakChunk {
  /** Caller-defined id echoed back on start/end so the caller knows which chunk is speaking. */
  id: string;
  text: string;
}

export interface TTSHandlers {
  onStart?: (id: string) => void;
  onEnd?: (id: string) => void;
  onError?: (id: string, error: unknown) => void;
}

/**
 * A single TTS engine: turns queued text chunks into audio. Both the browser's Web Speech synth
 * and the downloaded Kokoro model implement this same contract, so the player never knows which
 * one it is driving.
 */
export interface ITTSEngine {
  /** Load whatever the engine needs (voice list, or the neural model). Resolves when ready. */
  initialize(): Promise<void>;
  /** The voices this engine offers, flattened to the domain `Voice` shape. */
  voices(): Voice[];
  /** Register start/end/error callbacks. Call once before enqueuing. */
  setHandlers(handlers: TTSHandlers): void;
  /**
   * Queue a chunk to be spoken after anything already queued. Queued chunks play gaplessly — the
   * engine buffers/synthesizes the next while the current speaks. `onStart`/`onEnd` fire per id.
   */
  enqueue(chunk: SpeakChunk, opts: SpeakOptions): void;
  /** Clear the queue and cancel the current utterance. Pending start/end callbacks are dropped. */
  stop(): void;
  readonly ready: boolean;
}

/** The engines the user can pick between. `system` = the browser/OS Web Speech voices (instant,
    varies by device). `kokoro` = the downloaded neural model (uniform, higher quality, ~88MB once). */
export type TTSProviderId = 'system' | 'kokoro';

export interface TTSProvider {
  id: TTSProviderId;
  /** i18n message key for the human label. */
  labelKey: string;
  /** True if choosing this provider triggers a one-time model download. */
  requiresDownload: boolean;
}

export type TTSPhase = 'idle' | 'loading' | 'ready' | 'error';

/** Emitted as a provider loads so the UI can show a download bar and gate Play until it is ready. */
export interface TTSStatus {
  provider: TTSProviderId;
  phase: TTSPhase;
  /** 0..1 model load/download progress, meaningful while `phase === 'loading'`. */
  progress: number;
}

/**
 * The player-facing service: an engine that can also switch which provider is active. Exactly one
 * of these is injected into `PlayerStore`; in the real app it is `TTSRouter`, which owns both a
 * system engine and a Kokoro engine and delegates the `ITTSEngine` half to whichever is active.
 */
export interface ITTSService extends ITTSEngine {
  /** The providers the user can choose from, in display order. */
  providers(): TTSProvider[];
  /** Which provider is currently active. */
  activeProvider(): TTSProviderId;
  /**
   * Switch the active provider, loading it (and downloading its model on first use) if needed.
   * Resolves once the new provider is ready; rejects if its model fails to load.
   */
  setProvider(id: TTSProviderId): Promise<void>;
  /** Register a status listener for model load progress / ready / error. Call once. */
  onStatus(cb: (status: TTSStatus) => void): void;
}

export const TOKEN_ITTSService = token<ITTSService>('ITTSService');

/** The provider list shared by the router and the UI, in display order (system first: it is the
    zero-download default and every device has it). */
export const TTS_PROVIDERS: readonly TTSProvider[] = [
  { id: 'system', labelKey: 'voice.provider.system', requiresDownload: false },
  { id: 'kokoro', labelKey: 'voice.provider.kokoro', requiresDownload: true },
];

const VOICE_LOAD_TIMEOUT_MS = 1000;

export class TTSService implements ITTSEngine {
  private initialized = false;
  private platformVoices: SpeechSynthesisVoice[] = [];
  private handlers: TTSHandlers = {};

  async initialize(): Promise<void> {
    if (this.initialized) return;
    if (!('speechSynthesis' in window)) {
      throw new Error('Web Speech API is not available in this browser');
    }
    await this.loadVoices();
    this.initialized = true;
  }

  private loadVoices(): Promise<void> {
    return new Promise((resolve) => {
      let settled = false;
      const tryLoad = (): boolean => {
        this.platformVoices = window.speechSynthesis.getVoices();
        return this.platformVoices.length > 0;
      };
      const onChanged = (): void => finish();
      const finish = (): void => {
        if (settled) return;
        settled = true;
        tryLoad();
        window.speechSynthesis.removeEventListener('voiceschanged', onChanged);
        resolve();
      };

      if (tryLoad()) return resolve();
      window.speechSynthesis.addEventListener('voiceschanged', onChanged);
      // Some browsers never fire voiceschanged; don't hang boot forever.
      setTimeout(finish, VOICE_LOAD_TIMEOUT_MS);
    });
  }

  voices(): Voice[] {
    return this.platformVoices.map((v) => ({ id: v.voiceURI, name: v.name, lang: v.lang }));
  }

  setHandlers(handlers: TTSHandlers): void {
    this.handlers = handlers;
  }

  enqueue(chunk: SpeakChunk, opts: SpeakOptions): void {
    const utterance = new SpeechSynthesisUtterance(chunk.text);
    utterance.rate = opts.rate;
    const voice = opts.voiceId
      ? this.platformVoices.find((v) => v.voiceURI === opts.voiceId)
      : undefined;
    if (voice) utterance.voice = voice;

    utterance.onstart = () => this.handlers.onStart?.(chunk.id);
    utterance.onend = () => this.handlers.onEnd?.(chunk.id);
    utterance.onerror = (event) => {
      // Cancelling (stop/seek) fires 'interrupted'/'canceled'; that's intentional, not a failure.
      if (event.error === 'interrupted' || event.error === 'canceled') return;
      this.handlers.onError?.(chunk.id, event.error);
    };
    // Do NOT cancel here: queuing without cancelling is what makes back-to-back playback gapless.
    window.speechSynthesis.speak(utterance);
  }

  stop(): void {
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
  }

  get ready(): boolean {
    return this.initialized;
  }
}

/**
 * Test/headless double. Records spoken text and models the synth queue so a test can step playback
 * deterministically: `enqueue` starts the head if idle and fires `onStart`; `finishCurrent()`
 * simulates the active utterance ending (fires `onEnd`, then starts the next queued chunk).
 */
export class FakeTTSService implements ITTSService {
  readonly spoken: string[] = [];
  readonly enqueuedIds: string[] = [];
  lastRate: number | undefined;
  lastVoiceId: string | undefined;

  private _voices: Voice[];
  private handlers: TTSHandlers = {};
  private queue: string[] = [];
  private activeId: string | null = null;

  // Provider surface. The fake models a two-provider router: `voices()` returns the active
  // provider's list, so a test can `setProvider('kokoro')` and assert the voice list swapped.
  private _provider: TTSProviderId = 'system';
  private readonly voicesByProvider: Record<TTSProviderId, Voice[]>;
  private statusCb: ((status: TTSStatus) => void) | undefined;
  /** Set to reject `setProvider('kokoro')`, so a test can drive the model-load-failed path. */
  kokoroLoadError: Error | null = null;

  constructor(
    voices: Voice[] = [
      { id: 'v-alice', name: 'Alice', lang: 'en-US' },
      { id: 'v-bob', name: 'Bob', lang: 'en-GB' },
    ],
    kokoroVoices: Voice[] = [
      { id: 'af_heart', name: 'Heart', lang: 'en-us' },
      { id: 'bf_emma', name: 'Emma', lang: 'en-gb' },
    ],
  ) {
    this._voices = voices;
    this.voicesByProvider = { system: voices, kokoro: kokoroVoices };
  }

  initialize(): Promise<void> {
    return Promise.resolve();
  }

  voices(): Voice[] {
    return this._voices;
  }

  providers(): TTSProvider[] {
    return [...TTS_PROVIDERS];
  }

  activeProvider(): TTSProviderId {
    return this._provider;
  }

  async setProvider(id: TTSProviderId): Promise<void> {
    if (id === 'kokoro' && this.kokoroLoadError) {
      this.statusCb?.({ provider: id, phase: 'error', progress: 0 });
      throw this.kokoroLoadError;
    }
    this.stop();
    this._provider = id;
    this._voices = this.voicesByProvider[id];
    this.statusCb?.({ provider: id, phase: 'ready', progress: 1 });
  }

  onStatus(cb: (status: TTSStatus) => void): void {
    this.statusCb = cb;
  }

  /** Test helper: emit a synthetic status (e.g. a mid-download progress tick). */
  emitStatus(status: TTSStatus): void {
    this.statusCb?.(status);
  }

  setHandlers(handlers: TTSHandlers): void {
    this.handlers = handlers;
  }

  enqueue(chunk: SpeakChunk, opts: SpeakOptions): void {
    this.spoken.push(chunk.text);
    this.enqueuedIds.push(chunk.id);
    this.lastRate = opts.rate;
    this.lastVoiceId = opts.voiceId;
    this.queue.push(chunk.id);
    if (this.activeId === null) this.startHead();
  }

  stop(): void {
    this.queue = [];
    this.activeId = null;
  }

  /** Test helper: the active utterance finishes. Fires onEnd, then starts the next queued chunk. */
  finishCurrent(): void {
    if (this.activeId === null) return;
    const ended = this.activeId;
    this.queue.shift();
    this.activeId = null;
    this.handlers.onEnd?.(ended); // the caller may enqueue more here (which auto-starts the head)
    if (this.activeId === null) this.startHead();
  }

  /** Test helper: number of chunks still queued (currently playing + buffered ahead). */
  get queuedCount(): number {
    return this.queue.length;
  }

  /** Test helper: simulate a late/stale callback for an arbitrary id (e.g. an onend that lands
      after a seek already cancelled it), to prove the caller ignores stale generations. */
  simulateLateEnd(id: string): void {
    this.handlers.onEnd?.(id);
  }

  get ready(): boolean {
    return true;
  }

  private startHead(): void {
    this.activeId = this.queue[0] ?? null;
    if (this.activeId !== null) this.handlers.onStart?.(this.activeId);
  }
}
